import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export default function Header({ settings }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Shop", path: "/shop" },
    { label: "Warranty", path: "/warranty" },
    { label: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#faf9f7]/90 backdrop-blur-xl border-b border-[rgba(181,149,74,0.12)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="font-serif text-2xl text-[#0a0a0a] hover:opacity-70 transition-opacity"
          >
            {settings?.business_name || "PennyMacs"}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors ${
                  isActive(item.path)
                    ? "text-[#b5954a]"
                    : "text-[#6b6b6b] hover:text-[#0a0a0a]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Phone + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {settings?.phone && (
              <a
                href={`tel:${settings.phone}`}
                data-testid="header-phone"
                className="hidden sm:flex items-center gap-1.5 text-[13px] font-medium text-[#0a0a0a] hover:text-[#b5954a] transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                {settings.phone}
              </a>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav
            className="md:hidden pb-6 border-t border-[rgba(181,149,74,0.12)] pt-4 space-y-1"
            data-testid="mobile-nav"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={`block px-4 py-3 rounded-xl text-sm font-medium tracking-wide transition-colors ${
                  isActive(item.path)
                    ? "bg-[#f3f1ec] text-[#b5954a]"
                    : "text-[#6b6b6b] hover:bg-[#f3f1ec] hover:text-[#0a0a0a]"
                }`}
              >
                {item.label}
              </Link>
            ))}
            {settings?.phone && (
              <a
                href={`tel:${settings.phone}`}
                data-testid="mobile-phone-link"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#0a0a0a]"
              >
                <Phone className="w-4 h-4 text-[#b5954a]" />
                {settings.phone}
              </a>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
